import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { deepseekApi } from '@/lib/deepseekApi';
import { Building2, Send, CheckCircle2, ArrowLeft, Loader2 } from 'lucide-react';

export default function ExternalSubmission() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    companyName: '',
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    problemTitle: '',
    problemDescription: '',
    industry: '',
    desiredSolution: '',
    compensationOffered: '',
    additionalInfo: ''
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      // Get AI analysis of the problem
      const analysis = await deepseekApi.analyzeProblemSubmission(formData);
      
      // Store submission with analysis
      localStorage.setItem('lastSubmission', JSON.stringify({
        ...formData,
        submittedAt: new Date().toISOString(),
        analysis
      }));

      // In production, send to backend
      // await fetch('/api/partner-submissions', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify({ ...formData, analysis })
      // });

      setSubmitted(true);
    } catch (error) {
      setError('Submission failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#05070A] flex items-center justify-center p-4">
        <Card className="max-w-md bg-[#0A0C12] border-slate-800">
          <CardContent className="p-8 text-center">
            <div className="flex justify-center mb-4">
              <div className="p-3 bg-blue-500/10 rounded-full">
                <CheckCircle2 className="h-12 w-12 text-blue-400" />
              </div>
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Submission Received!</h2>
            <p className="text-slate-400 mb-6">
              Our analysts will review your problem within 3-5 business days.
            </p>
            <Button asChild className="bg-blue-500 hover:bg-blue-600">
              <Link to="/forge">Return to Forge</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#05070A] py-12">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <Button variant="ghost" className="mb-6 text-slate-400 hover:text-white" asChild>
          <Link to="/forge">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Forge
          </Link>
        </Button>

        <Card className="bg-[#0A0C12] border-slate-800">
          <CardHeader>
            <div className="flex items-center gap-3 mb-2">
              <Building2 className="h-8 w-8 text-blue-400" />
              <CardTitle className="text-2xl text-white">Partner Intake</CardTitle>
            </div>
            <CardDescription className="text-slate-400">
              Have a business problem that needs solving? Submit it here. Student squads will compete to build solutions.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {error && (
              <Alert variant="destructive" className="mb-6">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-white">Company Information</h3>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="companyName" className="text-slate-200">Company Name *</Label>
                    <Input
                      id="companyName"
                      value={formData.companyName}
                      onChange={(e) => setFormData({...formData, companyName: e.target.value})}
                      className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-600"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="industry" className="text-slate-200">Industry *</Label>
                    <Input
                      id="industry"
                      placeholder="e.g., FinTech, Healthcare, Retail"
                      value={formData.industry}
                      onChange={(e) => setFormData({...formData, industry: e.target.value})}
                      className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-600"
                      required
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="contactName" className="text-slate-200">Contact Name *</Label>
                    <Input
                      id="contactName"
                      value={formData.contactName}
                      onChange={(e) => setFormData({...formData, contactName: e.target.value})}
                      className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-600"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contactEmail" className="text-slate-200">Contact Email *</Label>
                    <Input
                      id="contactEmail"
                      type="email"
                      value={formData.contactEmail}
                      onChange={(e) => setFormData({...formData, contactEmail: e.target.value})}
                      className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-600"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contactPhone" className="text-slate-200">Contact Phone</Label>
                  <Input
                    id="contactPhone"
                    value={formData.contactPhone}
                    onChange={(e) => setFormData({...formData, contactPhone: e.target.value})}
                    className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-600"
                  />
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-white">Problem Details</h3>
                
                <div className="space-y-2">
                  <Label htmlFor="problemTitle" className="text-slate-200">Problem Title *</Label>
                  <Input
                    id="problemTitle"
                    placeholder="e.g., Customer onboarding takes too long"
                    value={formData.problemTitle}
                    onChange={(e) => setFormData({...formData, problemTitle: e.target.value})}
                    className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-600"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="problemDescription" className="text-slate-200">Problem Description *</Label>
                  <Textarea
                    id="problemDescription"
                    rows={4}
                    placeholder="Describe the problem in detail, including current challenges and impact on your business..."
                    value={formData.problemDescription}
                    onChange={(e) => setFormData({...formData, problemDescription: e.target.value})}
                    className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-600"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="desiredSolution" className="text-slate-200">Desired Solution (Optional)</Label>
                  <Textarea
                    id="desiredSolution"
                    rows={3}
                    placeholder="What would an ideal solution look like?"
                    value={formData.desiredSolution}
                    onChange={(e) => setFormData({...formData, desiredSolution: e.target.value})}
                    className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-600"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="compensationOffered" className="text-slate-200">Compensation Offered *</Label>
                  <Input
                    id="compensationOffered"
                    placeholder="e.g., $10K pilot project, equity, paid contract"
                    value={formData.compensationOffered}
                    onChange={(e) => setFormData({...formData, compensationOffered: e.target.value})}
                    className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-600"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="additionalInfo" className="text-slate-200">Additional Information</Label>
                  <Textarea
                    id="additionalInfo"
                    rows={3}
                    placeholder="Any other details, resources, or context?"
                    value={formData.additionalInfo}
                    onChange={(e) => setFormData({...formData, additionalInfo: e.target.value})}
                    className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-600"
                  />
                </div>
              </div>

              <Alert className="bg-blue-500/5 border-blue-500/20">
                <AlertDescription className="text-blue-400">
                  Our analysts will review your submission within 3-5 business days.
                </AlertDescription>
              </Alert>

              <Button 
                type="submit" 
                className="w-full bg-blue-500 hover:bg-blue-600"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send className="mr-2 h-4 w-4" />
                    Submit Challenge
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}